import { useEffect, useState } from "react";
import { Palette, Check } from "lucide-react";
import { api } from "@/lib/api";

const STORAGE_KEY = "ssp_brand_theme";

const THEMES = [
  {
    id: "ssp",
    label: "ServiceSell PRO",
    sub: "Dark + lime green (default)",
    swatch: ["#0F0F11", "#D2E031"],
  },
  {
    id: "toyota",
    label: "Premium Red",
    sub: "Showroom grey + dealer red",
    swatch: ["#474747", "#EB0A1E"],
  },
];

function applyTheme(id) {
  const root = document.documentElement;
  if (id === "ssp") root.removeAttribute("data-theme");
  else root.setAttribute("data-theme", id);
}

/**
 * Initialises the theme as early as possible — call once in App.js. Reads the
 * persisted choice from localStorage so the page never flashes the wrong
 * palette before React mounts the toggle.
 */
export function initBrandTheme() {
  try {
    const id = localStorage.getItem(STORAGE_KEY) || "ssp";
    applyTheme(id);
  } catch { /* SSR / private mode */ }
}

/**
 * Compact theme picker — pops a 2-option menu. Use in the dashboard header.
 *
 * Persistence model:
 *   1. localStorage paints the cached theme immediately (no FOUC).
 *   2. On mount we hydrate from GET /workshop/brand-theme — the canonical
 *      per-dealer value — and sync localStorage if the server disagrees.
 *   3. choose() PATCHes the new value to the server. localStorage is also
 *      updated so the next reload paints fast.
 */
export default function ThemeToggle() {
  const [theme, setTheme] = useState("ssp");
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  // 1) Paint the cached theme immediately, then 2) hydrate from server.
  useEffect(() => {
    try {
      const cached = localStorage.getItem(STORAGE_KEY) || "ssp";
      setTheme(cached);
      applyTheme(cached);
    } catch { /* ignore */ }

    let cancelled = false;
    api.get("/workshop/brand-theme")
      .then((r) => {
        const serverTheme = r?.data?.theme || "ssp";
        if (cancelled) return;
        setTheme(serverTheme);
        applyTheme(serverTheme);
        try { localStorage.setItem(STORAGE_KEY, serverTheme); } catch { /* ignore */ }
      })
      .catch(() => { /* not logged in / no workshop — keep localStorage value */ });
    return () => { cancelled = true; };
  }, []);

  // Close menu on outside click
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (!e.target.closest?.("[data-theme-toggle]")) setOpen(false);
    };
    window.addEventListener("click", onClick);
    return () => window.removeEventListener("click", onClick);
  }, [open]);

  const choose = async (id) => {
    setTheme(id);
    applyTheme(id);
    try { localStorage.setItem(STORAGE_KEY, id); } catch { /* ignore */ }
    setOpen(false);
    setSaving(true);
    try {
      await api.patch("/workshop/brand-theme", { theme: id });
    } catch { /* offline / not logged in — localStorage still holds */ }
    finally { setSaving(false); }
  };

  const active = THEMES.find((t) => t.id === theme) || THEMES[0];

  return (
    <div className="relative" data-theme-toggle>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-border hover:bg-muted text-foreground text-sm font-bold transition-colors"
        title="Switch brand theme — saved to your dealership"
        data-testid="theme-toggle-btn"
        disabled={saving}
      >
        <Palette className="w-4 h-4" />
        <span className="hidden lg:inline">{active.label}</span>
        <div className="flex items-center gap-0.5 ml-1">
          <span className="w-2 h-2 rounded-full" style={{ background: active.swatch[0] }} />
          <span className="w-2 h-2 rounded-full" style={{ background: active.swatch[1] }} />
        </div>
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-64 rounded-lg border border-border bg-card shadow-2xl overflow-hidden z-50 animate-fade-up"
          data-testid="theme-toggle-menu"
        >
          <div className="px-4 py-3 border-b border-border">
            <div className="text-xs font-bold uppercase tracking-wider text-primary">Brand theme</div>
            <div className="text-[11px] text-muted-foreground mt-0.5">
              Saved to your dealership — every advisor sees the same palette
            </div>
          </div>
          {THEMES.map((t) => {
            const isActive = t.id === theme;
            return (
              <button
                key={t.id}
                onClick={() => choose(t.id)}
                className={`w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-muted transition-colors ${isActive ? "bg-muted/50" : ""}`}
                data-testid={`theme-option-${t.id}`}
              >
                <div className="flex items-center gap-1 shrink-0">
                  <span className="w-4 h-4 rounded-full border border-border" style={{ background: t.swatch[0] }} />
                  <span className="w-4 h-4 rounded-full border border-border" style={{ background: t.swatch[1] }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold">{t.label}</div>
                  <div className="text-[11px] text-muted-foreground">{t.sub}</div>
                </div>
                {isActive && <Check className="w-4 h-4 text-primary shrink-0" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
